import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";

export const CartDrawer = () => {
  const { items, drawerOpen, setDrawerOpen, setQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  const close = () => setDrawerOpen(false);

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#0C0C0F]"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <div className="font-mono text-xs tracking-[0.3em] text-[#E8E9EB]" data-testid="cart-drawer-title">
                YOUR CART
              </div>
              <button onClick={close} data-testid="cart-close-button" className="text-slate-400 transition-colors duration-200 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 ? (
                <p className="mt-16 text-center text-sm text-slate-500" data-testid="cart-empty-message">
                  Your cart is empty.
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  {items.map((i) => (
                    <div key={i.id} className="glass rounded-xl p-4" data-testid={`cart-item-${i.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-display text-sm font-semibold text-white">{i.name}</div>
                          <div className="mt-1 font-mono text-[10px] tracking-[0.15em] text-slate-500">
                            {i.code} / {i.dose}
                          </div>
                        </div>
                        <button onClick={() => removeItem(i.id)} data-testid={`cart-remove-${i.id}`} className="text-slate-500 transition-colors duration-200 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-qty-minus-${i.id}`} className="rounded-md border border-white/10 p-1 text-slate-300 hover:border-[#E8E9EB]/50">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="font-mono text-sm text-white" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                          <button onClick={() => setQty(i.id, i.qty + 1)} data-testid={`cart-qty-plus-${i.id}`} className="rounded-md border border-white/10 p-1 text-slate-300 hover:border-[#E8E9EB]/50">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="font-mono text-sm text-[#E8E9EB]">{money(i.price * i.qty)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5">
                <div className="mb-4 flex items-center justify-between font-mono text-sm">
                  <span className="text-slate-400">SUBTOTAL</span>
                  <span className="text-white" data-testid="cart-subtotal">{money(subtotal)}</span>
                </div>
                <button
                  onClick={() => { close(); navigate("/checkout"); }}
                  data-testid="proceed-to-checkout-button"
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#6E7683] py-3.5 font-mono text-xs tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811]"
                >
                  PROCEED TO CHECKOUT
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
